import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ColDef,
  colorSchemeDark,
  colorSchemeLight,
  themeQuartz,
} from "ag-grid-community";
import { fetchRateComponents } from "../../../api/routes";
import {
  RateComponentsAddressEntry,
  RateComponentsResponse,
  RateCoverageComponents,
} from "../../../client/types.gen";
import { formatExactMoneyDollars } from "../Money";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { ThemeContext } from "../../../themes/Theme";
import {
  computeContributionWeightedAvg,
  FactorRow,
} from "./contributionWeightedAvg";
import { formatSicDisplay } from "../sov/sic";

type Props = {
  reportId: string;
  batchId: string | null;
  visible: boolean;
  onHide: () => void;
};

type CoverageName = RateComponentsResponse["coverage_keys"][number];

const COVERAGE_LABELS: Record<CoverageName, string> = {
  BGI: "BGI",
  BGII: "BGII",
  SCL: "Special (SCL)",
};

const agThemeLight = themeQuartz.withPart(colorSchemeLight);
const agThemeDark = themeQuartz.withPart(colorSchemeDark);

const fmtMoney = (n: number | null | undefined) =>
  n === null || n === undefined
    ? ""
    : formatExactMoneyDollars(n, { withRounding: true, withDollarSign: true });

const fmtFactor = (n: number | null | undefined) =>
  n === null || n === undefined ? "" : n.toFixed(3);

// Loss Cost is in $ per $100.
const fmtBaseRateDollars = (n: number | null | undefined) =>
  n === null || n === undefined ? "" : `$${n.toFixed(3)}`;

const factorColorStyle = (value: number | undefined) => {
  if (value === undefined) return undefined;
  if (value > 1.0001) return { color: "var(--red-500)" };
  if (value < 0.9999) return { color: "var(--green-500)" };
  return undefined;
};

const AccountImpactLabel: React.FC = () => (
  <span style={{ fontWeight: "bold" }}>
    Overall Account Impact{" "}
    <TooltipWrapper
      tooltipText="This value, if applied to every location, would leave the premium unchanged. It is one measure of account level impact of this factor."
      showDelay={200}
    >
      <i
        className="pi pi-question-circle"
        style={{
          fontSize: "0.75rem",
          color: "var(--text-color-secondary)",
          cursor: "help",
        }}
      />
    </TooltipWrapper>
  </span>
);

const pinnedFactorCellStyle = (p: {
  value: number | undefined;
  node?: { rowPinned?: string | null };
}) => ({
  ...factorColorStyle(p.value),
  ...(p.node?.rowPinned ? { fontWeight: "bold" as const } : {}),
});

const accountImpactAddressCol = <
  T extends { addr: RateComponentsAddressEntry },
>(): ColDef<T>[] => [
  {
    headerName: "Address",
    pinned: "left",
    minWidth: 260,
    valueGetter: (p) =>
      p.node?.rowPinned
        ? "Overall Account Impact"
        : p.data
          ? addressLabel(p.data.addr)
          : "",
    cellRenderer: (p: { value: string; node: { rowPinned: string | null } }) =>
      p.node.rowPinned ? <AccountImpactLabel /> : p.value,
    cellStyle: (p) => (p.node?.rowPinned ? { fontWeight: "bold" } : null),
  },
  {
    headerName: "SIC",
    pinned: "left",
    width: 80,
    minWidth: 80,
    valueGetter: (p) =>
      p.node?.rowPinned
        ? ""
        : p.data
          ? formatSicDisplay(p.data.addr.sic_code)
          : "",
    cellStyle: (p) => (p.node?.rowPinned ? { fontWeight: "bold" } : null),
  },
];

const computePremium = (comp: RateCoverageComponents): number => {
  let product = comp.base_rate;
  for (const v of Object.values(comp.factors)) {
    product *= v;
  }
  return product * (comp.building_value / 100);
};

const addressLabel = (addr: RateComponentsAddressEntry): string => {
  const pieces: string[] = [];
  if (addr.street_address) pieces.push(addr.street_address);
  const cityState = [addr.city, addr.state].filter(Boolean).join(", ");
  if (cityState) pieces.push(cityState);
  if (pieces.length === 0) {
    return addr.location_name || addr.premises_number || addr.address_id;
  }
  return pieces.join(" • ");
};

const collectFactorNames = (
  addresses: RateComponentsAddressEntry[],
  coverage: string
): string[] => {
  const seen = new Set<string>();
  const ordered: string[] = [];
  const hasNonOne = new Set<string>();
  for (const addr of addresses) {
    const comp = addr.components[coverage];
    if (!comp) continue;
    for (const [name, value] of Object.entries(comp.factors)) {
      if (!seen.has(name)) {
        seen.add(name);
        ordered.push(name);
      }
      if (Math.abs(value - 1) > 0.0001) {
        hasNonOne.add(name);
      }
    }
  }
  return ordered.filter((name) => hasNonOne.has(name));
};

type Row = {
  addr: RateComponentsAddressEntry;
  comp: RateCoverageComponents;
};

type TotalRow = {
  addr: RateComponentsAddressEntry;
  buildingValue: number;
  totalPremium: number;
  perCoverage: Record<string, number>;
  weightedFactors: Record<string, number>;
};

// Weight each coverage's factor value by that coverage's premium share.
// Coverages that don't carry the factor contribute with value=1 so a factor
// that only appears in one coverage is diluted proportionally by the others.
const computeWeightedFactors = (
  addr: RateComponentsAddressEntry,
  perCoverage: Record<string, number>,
  totalPremium: number,
  coverageKeys: readonly CoverageName[]
): Record<string, number> => {
  if (totalPremium <= 0) return {};
  const names = new Set<string>();
  for (const key of coverageKeys) {
    const comp = addr.components[key];
    if (!comp) continue;
    for (const n of Object.keys(comp.factors)) names.add(n);
  }
  const result: Record<string, number> = {};
  for (const name of names) {
    let weighted = 0;
    for (const key of coverageKeys) {
      const premium = perCoverage[key] ?? 0;
      const comp = addr.components[key];
      const f = comp?.factors[name] ?? 1;
      weighted += premium * f;
    }
    result[name] = weighted / totalPremium;
  }
  return result;
};

const collectWeightedFactorNames = (rows: TotalRow[]): string[] => {
  const seen = new Set<string>();
  const ordered: string[] = [];
  const hasNonOne = new Set<string>();
  for (const r of rows) {
    for (const [name, v] of Object.entries(r.weightedFactors)) {
      if (!seen.has(name)) {
        seen.add(name);
        ordered.push(name);
      }
      if (Math.abs(v - 1) > 0.0001) hasNonOne.add(name);
    }
  }
  return ordered.filter((name) => hasNonOne.has(name));
};

const TotalTable: React.FC<{
  addresses: RateComponentsAddressEntry[];
  coverageKeys: readonly CoverageName[];
  theme: typeof agThemeLight;
}> = ({ addresses, coverageKeys, theme }) => {
  const rows: TotalRow[] = useMemo(
    () =>
      addresses.map((addr) => {
        const perCoverage: Record<string, number> = {};
        let totalPremium = 0;
        let buildingValue = 0;
        for (const key of coverageKeys) {
          const comp = addr.components[key];
          if (!comp) continue;
          const premium = computePremium(comp);
          perCoverage[key] = premium;
          totalPremium += premium;
          if (comp.building_value > buildingValue)
            buildingValue = comp.building_value;
        }
        const weightedFactors = computeWeightedFactors(
          addr,
          perCoverage,
          totalPremium,
          coverageKeys
        );
        return {
          addr,
          buildingValue,
          totalPremium,
          perCoverage,
          weightedFactors,
        };
      }),
    [addresses, coverageKeys]
  );

  const factorNames = useMemo(() => collectWeightedFactorNames(rows), [rows]);

  const pinnedTopRowData: TotalRow[] = useMemo(() => {
    if (rows.length === 0 || factorNames.length === 0) return [];
    // Flatten (address, coverage) pairs into FactorRows, filling missing
    // factors with 1 so every row has the same factor set.
    const flatRows: FactorRow[] = [];
    for (const addr of addresses) {
      for (const key of coverageKeys) {
        const comp = addr.components[key];
        if (!comp) continue;
        const filledFactors: Record<string, number> = {};
        for (const name of factorNames) {
          filledFactors[name] = comp.factors[name] ?? 1;
        }
        flatRows.push({
          comp: { ...comp, factors: filledFactors },
        });
      }
    }
    const avgFactors = computeContributionWeightedAvg(flatRows, factorNames);
    return [
      {
        addr: { address_id: "", components: {} },
        buildingValue: 0,
        totalPremium: 0,
        perCoverage: {},
        weightedFactors: avgFactors,
      },
    ];
  }, [addresses, coverageKeys, rows, factorNames]);

  const columnDefs: ColDef<TotalRow>[] = useMemo(() => {
    const cols: ColDef<TotalRow>[] = [
      ...accountImpactAddressCol<TotalRow>(),
      {
        headerName: "Building Value",
        type: "rightAligned",
        valueGetter: (p) => (p.node?.rowPinned ? null : p.data?.buildingValue),
        valueFormatter: (p) => fmtMoney(p.value),
      },
      ...coverageKeys.map<ColDef<TotalRow>>((key) => ({
        headerName: `${key} Premium`,
        type: "rightAligned",
        valueGetter: (p) =>
          p.node?.rowPinned ? null : p.data?.perCoverage[key],
        valueFormatter: (p) => fmtMoney(p.value),
      })),
      {
        headerName: "Total Premium",
        type: "rightAligned",
        cellStyle: { fontWeight: "bold" },
        valueGetter: (p) => (p.node?.rowPinned ? null : p.data?.totalPremium),
        valueFormatter: (p) => fmtMoney(p.value),
      },
      {
        headerName: "Rate ($/$100)",
        type: "numericColumn",
        valueGetter: (p) => {
          if (p.node?.rowPinned) return null;
          if (!p.data || p.data.buildingValue <= 0) return null;
          return (p.data.totalPremium * 100) / p.data.buildingValue;
        },
        valueFormatter: (p) => fmtBaseRateDollars(p.value),
      },
      ...factorNames.map<ColDef<TotalRow>>((name) => ({
        headerName: name,
        type: "rightAligned",
        valueGetter: (p) => p.data?.weightedFactors[name],
        valueFormatter: (p) => fmtFactor(p.value),
        cellStyle: pinnedFactorCellStyle,
      })),
    ];
    return cols;
  }, [coverageKeys, factorNames]);

  if (rows.length === 0) {
    return <div>No premium data available.</div>;
  }

  return (
    <div style={{ height: "70vh" }}>
      <AgGridReact<TotalRow>
        theme={theme}
        modules={[AllCommunityModule]}
        rowData={rows}
        columnDefs={columnDefs}
        pinnedTopRowData={pinnedTopRowData}
        headerHeight={40}
        rowHeight={32}
      />
    </div>
  );
};

const CoverageTable: React.FC<{
  coverage: string;
  addresses: RateComponentsAddressEntry[];
  theme: typeof agThemeLight;
}> = ({ coverage, addresses, theme }) => {
  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    for (const addr of addresses) {
      const comp = addr.components[coverage];
      if (comp) out.push({ addr, comp });
    }
    return out;
  }, [coverage, addresses]);

  const factorNames = useMemo(
    () => collectFactorNames(addresses, coverage),
    [addresses, coverage]
  );

  const pinnedTopRowData: Row[] = useMemo(() => {
    if (rows.length === 0 || factorNames.length === 0) return [];
    const avgFactors = computeContributionWeightedAvg(rows, factorNames);
    return [
      {
        addr: { address_id: "", components: {} },
        comp: { base_rate: 0, factors: avgFactors, building_value: 0 },
      },
    ];
  }, [rows, factorNames]);

  const columnDefs: ColDef<Row>[] = useMemo(() => {
    const cols: ColDef<Row>[] = [
      ...accountImpactAddressCol<Row>(),
      {
        headerName: "Base Rate ($/$100)",
        type: "numericColumn",
        valueGetter: (p) => (p.node?.rowPinned ? null : p.data?.comp.base_rate),
        valueFormatter: (p) => fmtBaseRateDollars(p.value),
      },
      ...factorNames.map<ColDef<Row>>((name) => ({
        headerName: name,
        type: "rightAligned",
        valueGetter: (p) => p.data?.comp.factors[name],
        valueFormatter: (p) => fmtFactor(p.value),
        cellStyle: pinnedFactorCellStyle,
      })),
      {
        headerName: "Building Value",
        type: "rightAligned",
        valueGetter: (p) =>
          p.node?.rowPinned ? null : p.data?.comp.building_value,
        valueFormatter: (p) => fmtMoney(p.value),
      },
      {
        headerName: "Premium",
        type: "rightAligned",
        cellStyle: { fontWeight: "bold" },
        valueGetter: (p) =>
          p.node?.rowPinned
            ? null
            : p.data
              ? computePremium(p.data.comp)
              : null,
        valueFormatter: (p) => fmtMoney(p.value),
      },
      {
        headerName: "Rate ($/$100)",
        type: "numericColumn",
        valueGetter: (p) => {
          if (p.node?.rowPinned) return null;
          if (!p.data || p.data.comp.building_value <= 0) return null;
          return (
            (computePremium(p.data.comp) * 100) / p.data.comp.building_value
          );
        },
        valueFormatter: (p) => fmtBaseRateDollars(p.value),
      },
    ];
    return cols;
  }, [factorNames]);

  if (rows.length === 0) {
    return <div>No {coverage} premium data available.</div>;
  }

  return (
    <div style={{ height: "70vh" }}>
      <AgGridReact<Row>
        theme={theme}
        modules={[AllCommunityModule]}
        rowData={rows}
        columnDefs={columnDefs}
        pinnedTopRowData={pinnedTopRowData}
        headerHeight={40}
        rowHeight={32}
      />
    </div>
  );
};

export const RateComponentsDialog: React.FC<Props> = ({
  reportId,
  batchId,
  visible,
  onHide,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<RateComponentsAddressEntry[]>([]);
  const [coverageKeys, setCoverageKeys] = useState<CoverageName[]>([]);
  const { theme } = useContext(ThemeContext);
  const agTheme = theme === "light" ? agThemeLight : agThemeDark;

  useEffect(() => {
    if (!visible || !batchId) return;
    setLoading(true);
    setError(null);
    setAddresses([]);
    setCoverageKeys([]);
    fetchRateComponents(reportId, batchId)
      .then((resp) => {
        setAddresses(resp.addresses);
        setCoverageKeys(resp.coverage_keys);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Failed to load rate data");
      })
      .finally(() => setLoading(false));
  }, [visible, reportId, batchId]);

  return (
    <Dialog
      header="Rate Components"
      visible={visible}
      onHide={onHide}
      draggable={false}
      style={{ width: "95vw", maxWidth: "1800px" }}
    >
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <ProgressSpinner />
        </div>
      )}
      {error && (
        <div style={{ color: "var(--red-500)", padding: 12 }}>{error}</div>
      )}
      {!loading && !error && addresses.length === 0 && (
        <div style={{ padding: 12 }}>
          No rate component data available for this run. Generate a new rater
          run to populate this view.
        </div>
      )}
      {!loading && !error && addresses.length > 0 && (
        <TabView>
          <TabPanel key="total" header="Total">
            <TotalTable
              addresses={addresses}
              coverageKeys={coverageKeys}
              theme={agTheme}
            />
          </TabPanel>
          {coverageKeys.map((key) => (
            <TabPanel key={key} header={COVERAGE_LABELS[key]}>
              <CoverageTable
                coverage={key}
                addresses={addresses}
                theme={agTheme}
              />
            </TabPanel>
          ))}
        </TabView>
      )}
    </Dialog>
  );
};
